import {
  Component,
  OnInit,
  effect,
  forwardRef,
  inject,
  input,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ExpandableComponent } from '../expandable-component/expandable.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { TextButtonComponent } from '../text-button/text-button.component';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { IconComponent } from '../icons/icon.component';
import { AuditService } from '../../core/services/audit.service';

@Component({
  selector: 'cwo-special-instructions-accordion',
  standalone: true,
  imports: [FormsModule, ExpandableComponent, TextButtonComponent, CommonModule, TranslocoModule, IconComponent],
  templateUrl: './special-instructions-accordion.component.html',
  styleUrl: './special-instructions-accordion.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SpecialInstructionsAccordionComponent),
      multi: true,
    },
  ],
})
export class SpecialInstructionsAccordionComponent
  implements ControlValueAccessor, OnInit
{

  ariaLabel = input<string>('Add Special Instructions');
  labelColor = input<string>('var(--primary-dark)');
  instructionHelpText = input<string>('');
  title = input<string>('');
  description = input<string>('');
  descriptionPlaceholder = input<string>('');
  maxLength = input<number>(100);
  needsOrderInstructions = input<boolean>(false);
  iconName = IconNameType;
  expanded = false;
  text: string = '';
  characterCount: number = 0;
  hasError: boolean = false;
  submited = false;
  iconNameType = IconNameType;
  showAlwaysExpandButton = input<boolean>(false);
  removeBorder = input<boolean>(false);
  itemInstructionsExpanded = input<boolean>(false);
  auditService = inject(AuditService)
  translocoService = inject(TranslocoService);

  private _uid = Math.random().toString(36).slice(2, 9);
  labelId = 'instructions-label-' + this._uid;
  textareaId = 'special-instructions-textarea-' + this._uid;
  
  readonly parentExpanded = input(true);

  readonly instructionsChanged = output<string>();
  readonly instructionsBlurred = output<string>();

  constructor() {
    effect(() => {
      this.text = this.description();
    });
  }

  ngOnInit(): void {
    this.characterCount = this.description().length;
    if(this.characterCount > 0 || this.itemInstructionsExpanded()) {
      this.expanded = true;
    }
  }

  handleExpansion(value: boolean) {
    this.expanded = value;
          this.auditService.createAudit(() => `Special instructions have been ${this.expanded ? 'expanded' : 'collapsed'}.`);



    if (this.expanded) {
      const modalContent = document.getElementById(
        'item-editor-scrollable-wrapper'
      );
      const el = document.getElementById('item-editor-special-instructions');
      const elContent = document.getElementById(
        'item-editor-special-instructions-content'
      );
      const footer = document.getElementById('item-editor-footer');

      if (el && elContent && modalContent && footer) {
        const modalContentRect = modalContent.getBoundingClientRect();
        const elContentRect = elContent.getBoundingClientRect();
        const footerRect = footer.getBoundingClientRect();

        // Calculate the visible area within the modal content
        const visibleAreaHeight = modalContentRect.height - footerRect.height;

        // Check if the content's bottom position exceeds the visible area
        if (elContentRect.bottom > visibleAreaHeight) {
          // Wait for the category to be fully expanded before scrolling
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
    }
  }

  onModelChange(val: any): void {
    if(!this.text) {
      this.text = '';
    }

    if(this.needsOrderInstructions() && this.submited && val.length === 0) {
      this.hasError = true;
    } else {
      this.hasError = false;
    }
    
    this.onChange(val);
    this.characterCount = this.text.length;    
    this.instructionsChanged.emit(val);
  }

  onChange = (val: any) => {
    return val;
  };

  onTouch = () => {
    return undefined;
  };

  onBlur(): void {
    this.onTouch();
    this.instructionsBlurred.emit(this.text);
  }

  writeValue(value: any): void {
    if (value == null) {
      return;
    }

    this.text = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
}
